import { Component } from '@angular/core';
import {
  SharedFacade,
  customDecode,
  customDecrypt,
  customEncode,
  customEncrypt,
  decrypt,
  encrypt,
} from '@piqet-angular/data';
import { ColDef } from 'ag-grid-community';
import { ActionButtonComponent } from 'libs/ui/src/lib/action-button/action-button.component';
import { GridIconComponent } from 'libs/ui/src/lib/grid-icon/grid-icon.component';
import { ProductManufactureDataGridService } from '../product-manufacture/product-manufacturing-data-grid.service';
import {
  AccessPermissionService,
  backToDashboardStr,
  CustomModalService,
  SwalService,
} from '@piqet-angular/shared';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, lastValueFrom } from 'rxjs';
import { SharedService } from 'libs/data/src/lib/services/shared/shared.service';
import { ProjectsService } from 'libs/data/src/lib/services/projects/projects.service';
import { ReportsService } from 'libs/data/src/lib/services/report/reports.service';

@Component({
  selector: 'piqet-angular-product-use',
  templateUrl: './product-use.component.html',
  styleUrls: ['./product-use.component.scss'],
})
export class ProductUseComponent {
  columnDefs!: ColDef[];
  headerData = {
    name: 'Product use',
  };
  frameworkComponents = {
    actionRenderer: ActionButtonComponent,
    iconsOfTransport: GridIconComponent,
  };
  prodUseList: any;
  activatedRoute: any;
  editPopUp!: Subscription;
  menuDataObs!: Subscription;
  menuDataForRouting: any;
  nextRouteName: any;
  productId: any;
  backRouteType: any;
  btnName: any;
  productConfigId: any;
  backBtnLabel: string = '';
  showEdit: boolean = false;
  activeProductProjectId: any;
  constructor(
    public sharedFacade: SharedFacade,
    private prodManufactureDataGridService: ProductManufactureDataGridService,
    private modal: CustomModalService,
    private route: ActivatedRoute,
    private sharedService: SharedService,
    private accessService: AccessPermissionService,
    private router: Router,
    private projectService: ProjectsService,
    private reportsService: ReportsService,
    private swalService: SwalService
  ) {
    this.activatedRoute = this.route.snapshot.routeConfig?.path;
    this.route.queryParams.subscribe((values: any) => {
      const url = location.href.split('/');
      this.productConfigId = Number(atob(decodeURIComponent(customDecode(url[6]))));
      const productId = customDecrypt(this.route.snapshot.queryParams['param']);
      this.productId = Number(productId.id);
      this.getProductUseList();
    });
  }

  ngOnInit() {
    this.columnDefs = this.prodManufactureDataGridService.getProductManufactureDataColumn(
      this.activatedRoute
    );
    this.showEdit = this.accessService.hasPermission('edit', '');
    this.menuDataObs = this.sharedService.productProjectMenuArrData$.subscribe((res: any) => {
      if (res?.length) {
        this.menuDataForRouting = res.flatMap((product: any) =>
          product.submenu.map((scope: any) => ({
            ...scope,
            productId: product.productId,
            productName: product.name,
          }))
        );
      }
    });
    this.editPopUp = this.prodManufactureDataGridService.editProdUseData$.subscribe((data: any) => {
      this.openProdUseModal(data, 'Edit product use');
    });
    this.getProductUseList();
    this.findNextRoute();
  }

  async openProdUseModal(id: any, title: string) {
    const res = await lastValueFrom(this.projectService.getProductUseModalData(this.productId));
    let indexedData = res?.data?.productUses.map((x: any, i: any) => {
      return { ...x, index: x.id };
    });
    this.modal.invoke('addProdUse', {
      initialState: {
        label: title,
        manufacturingList: res?.data?.energyProcessItems,
        fillingList: res?.data?.fillingListItems,
        prodId: this.productId,
        productUseGridData: indexedData,
        isCostTrunOff: res?.data?.isTurnOffCost,
      },
      class: 'modal-xl',
    });
    this.modal.ref?.content.saveProdUse.subscribe(() => {
      this.getProductUseList();
    });
  }

  getProductUseList() {
    this.projectService.getProductUseDataList(this.productId).subscribe((res: any) => {
      this.prodUseList = res.data;
      this.btnName = res.data.length > 0 ? 'Edit product use' : 'Add product use';
    });
  }

  findNextRoute() {
    const currentProductId = this.productId; // Fetch this from route or localStorage
    const activeIndex = this.menuDataForRouting.findIndex(
      (x: any) => x.description === this.activatedRoute && x.productId === currentProductId
    );
    // Find the next index and route
    const nextIndex = activeIndex + 1;
    if (
      nextIndex < this.menuDataForRouting.length &&
      this.menuDataForRouting[nextIndex].productId === currentProductId
    ) {
      this.nextRouteName = this.menuDataForRouting[nextIndex].description;
    } else {
      this.nextRouteName = 'chart-at-glance'; // No next route
      this.activeProductProjectId = localStorage.getItem('productConfigId');
    }
    if (!this.nextRouteName) this.backBtnLabel = backToDashboardStr;
    else this.backBtnLabel = 'Next';
  }

  findPrevRoute() {
    const currentProductId = this.productId; // Fetch this from route or localStorage
    const activeIndex = this.menuDataForRouting.findIndex(
      (x: any) => x.description === this.activatedRoute && x.productId === currentProductId
    );

    // Find the next index and route
    const nextIndex = activeIndex - 1;
    if (
      nextIndex < this.menuDataForRouting.length &&
      this.menuDataForRouting[nextIndex].productId === currentProductId
    ) {
      this.backRouteType = this.menuDataForRouting[nextIndex].description;
    } else {
      this.backRouteType = 'configuration'; // No next route
    }
  }

  async nextRoute() {
    this.findNextRoute();
    if (!this.nextRouteName) this.router.navigate([`/dashboard`]);
    else {
      if (this.nextRouteName == 'chart-at-glance') {
        const activeProductProjectId: any = localStorage.getItem('productConfigId');
        const reportFormData: any = await this.reportsService.checkReportFromDataForProduct(
          activeProductProjectId
        );
        if (reportFormData && reportFormData.data) {
          const { item1, item2 } = reportFormData.data;

          if (item1 === false) {
            if (item2) {
              await this.swalService.confirmNew(
                'Report validation',
                item2,
                undefined,
                undefined,
                true
              );
            }
            return;
          } else {
            this.sharedService.accessProductSideBarData('charts');
            this.router.navigate([
              `projects/product-configuration/${customEncode(
                btoa(this.productConfigId)
              )}/chart-at-glance`,
            ]);
          }
        }
      } else {
        const encryptedValue = customEncrypt(this.productId);
        this.router.navigate(
          [
            `projects/product-configuration/${customEncode(btoa(this.productConfigId))}/${
              this.nextRouteName
            }`,
          ],
          { queryParams: { param: encryptedValue } }
        );
      }
    }
  }

  backRoute() {
    this.findPrevRoute();
    const encryptedValue = customEncrypt(this.productId);
    this.router.navigate(
      [
        `projects/product-configuration/${customEncode(btoa(this.productConfigId))}/${
          this.backRouteType
        }`,
      ],
      { queryParams: { param: encryptedValue } }
    );
  }

  ngOnDestroy() {
    this.editPopUp.unsubscribe();
    this.menuDataObs.unsubscribe();
  }
}
